import { useCallback, useEffect, useState } from "react";
import { bool, func } from "prop-types";
import { observer } from "mobx-react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { roundFloat } from "@repo/utils";
import { removeProjectCoverQuery } from "@query";
import { isBetween, SCALE_PRESETS, LOCKED_FEATURES, PDF_PROPOSAL_SIZE, PDF_PROPOSAL_MB } from "@utils";
import { useEditorWebsocket, useS3Uploader, useStores, useFeatureGuardian } from "@hooks";
import { useEstimateEditorStore, useEstimateEditorSettings, useEstimateComments, CommentsDrawer, Share } from "@tools";
import { ActionButton, Alert, Button, TextField } from "@components";
import { Grid, IconButton, MenuItem, Paper, Divider, Tooltip, Hidden } from "@material-ui/core";
import { DocumentRemoval } from "../DocumentRemoval";
import { AspectRatioVertical, AspectRatioHorizontal, Trash } from "@assets";
import { Add, MenuOpenRounded, Remove } from "@material-ui/icons";
import useStyle from "./ToolBar.style";
import { Exports } from "@tools/EstimateEditor/components/TableToolbar/components";

let scrollTimeout;

export const ToolBar = observer(({
  proposalSharing,
  noShareActions,
  noThumbButton,
  isObserver,
  hasSharePrivileges,
  sidebarVisible,
  handleShowSidebar,
}) => {
  const { t } = useTranslation();
  const { estimateUuid } = useParams();
  const { stateStore } = useStores();
  const editorStore = useEstimateEditorStore();
  const { allVisibleComments } = useEstimateComments();
  const socket = useEditorWebsocket();
  const { hasSellerPrivileges, isProposal } = useEstimateEditorSettings();
  const classes = useStyle();
  
  const { uploadProposal } = useS3Uploader();
  
  const { checkAccess, FeatureAlert } = useFeatureGuardian(
    LOCKED_FEATURES.PDF
  );
  
  const {
    pdfDocument,
    currentPage,
    numPages,
    scale,
    displayScale,
    scaleValue,
    pdfPages
  } = editorStore;
  
  const [ uploadAlertVisible, showUploadAlert ] = useState(false);
  const [ localCurrentPage, setCurrentPage ] = useState(currentPage || 1);
  const [ removeAlertVisible, showRemoveAlert ] = useState(false);
  
  const handleScaleChange = (e) => {
    const s = editorStore.setScale(e.target.value);
    if(s && hasSellerPrivileges)
      socket?.updateCoverScale?.(pdfDocument.id, s);
  };
  
  const handleScaleSet = (v) => () => {
    const s = editorStore.setScale(v);
    if(s && hasSellerPrivileges)
      socket?.updateCoverScale?.(pdfDocument.id, s);
  };
  
  const handleScaleButton = (d) => () => {
    const s = editorStore.setScale((parseInt(scale * 10) + d) / 10, SCALE_PRESETS.CUSTOM);
    if(s && hasSellerPrivileges)
      socket?.updateCoverScale?.(pdfDocument.id, s);
  };
  
  const applyWheelScale = useCallback(() => {
    editorStore.applyScale(editorStore.displayScale);
    if(hasSellerPrivileges)
      socket?.updateCoverScale?.(pdfDocument.id, { scale: editorStore.displayScale });
  }, [editorStore])
  
  const handleWheelScale = (e) => {
    if (pdfDocument && (e.ctrlKey || e.metaKey)) {
      e.stopPropagation();
      e.preventDefault();
      clearTimeout(scrollTimeout);
      
      const value = Math.max(Math.min(editorStore.displayScale + e.wheelDelta / 1200, 100), 0.1);
      
      editorStore.setScale(roundFloat(value), true, true);
      // scrollTimeout = setTimeout(applyWheelScale, 250);
      applyWheelScale();
    }
  };
  
  const handleArrowKeys = (e) => {
    const el = window.document.activeElement;
    if (
      [37, 39].includes(e.keyCode) &&
      el.nodeName !== "INPUT" &&
      !el.className.match(/(input)|(select)/gi)
    ) {
      const newPage = editorStore.currentPage + (e.key === "ArrowLeft" ? -1 : 1);
      if (isBetween(newPage, 0, editorStore.numPages-1))
        editorStore.jumpToPage(newPage);
    }
  };
  
  useEffect(() => {
    window.addEventListener("wheel", handleWheelScale, { passive: false });
    window.addEventListener("keydown", handleArrowKeys, { passive: false });
    return () => {
      window.removeEventListener("wheel", handleWheelScale, { passive: false });
      window.removeEventListener("keydown", handleArrowKeys, { passive: false });
    };
  }, []);
  
  useEffect(() => {
    setCurrentPage(currentPage+1);
  }, [currentPage]);
  
  const fileGetter = async (files) => {
    if(!files[0]) return;
    if (proposalSharing) {
      const allowed = await checkAccess();
      if (!allowed) return;
    }
    
    const file = files[0]
    let version = 1;
    
    stateStore.resetFilesUploadProgress(pdfDocument ? 2 : 1);
    if(pdfDocument?.file?.path) {
      await removeProjectCoverQuery(estimateUuid, pdfDocument.id);
      version = Number(decodeURIComponent(pdfDocument.file.path).match(/\/(\d+)\//)?.[1] || 0) + 1;
      socket?.removeCover();
      stateStore.setFilesUploadProgress(1, 100);
    }
    const pdf = await uploadProposal(estimateUuid, file, 0, version, [], proposalSharing ? 0 : -1, 1);
    stateStore.resetFilesUploadProgress();
    
    if(!pdf)
      return;
    
    socket?.addPdfCover(pdf);
    editorStore.setPdfDocument(pdf, true);
  }
  
  const handleCurrentPageChange = (e) => {
    const v = e.target.value.replace(/[^\d]/g, "");
    setCurrentPage(v);
  };
  
  const handlePageInputBlur = () => {
    if (
      !localCurrentPage ||
      !isBetween(parseInt(localCurrentPage), 1, numPages)
    )
      setCurrentPage(currentPage);
    else editorStore.jumpToPage(parseInt(localCurrentPage)-1);
  };
  
  const { getInputProps, open } = useDropzone({
    accept: {
      "application/pdf": [".pdf"]
    },
    multiple: false,
    noClick: true,
    noKeyboard: true,
    maxSize: PDF_PROPOSAL_SIZE,
    onDropAccepted: fileGetter,
    onDropRejected: () => showUploadAlert(true),
  });

  return (
    <Paper
      elevation={1}
      className={classes.root}
    >
      <Grid
        item
        container
        justifyContent="space-between"
        alignItems="center"
        wrap="nowrap"
        className="h-full"
      >
        <Grid
          item
          container
          alignItems="center"
          wrap="nowrap"
          className={classes.filler}
        >
          {!noThumbButton && (
            <Hidden xsDown>
              <Tooltip
                title={t(
                  `views.pdf_viewer.tooltips.${
                    sidebarVisible ? "hide" : "show"
                  }`
                )}
              >
                <ActionButton
                  icon={<MenuOpenRounded />}
                  size="small"
                  color="secondary"
                  onClick={handleShowSidebar}
                />
              </Tooltip>
            </Hidden>
          )}
        </Grid>
        
        <Grid
          item
          container
          wrap="nowrap"
          justifyContent="center"
          alignItems="center"
        >
          {
            isObserver &&
            <>
              <Button
                id="uploadPdf"
                variant="contained"
                onClick={open}
                size="small"
                className="mr-2"
              >
                {t(
                  `views.pdf_viewer.import.${
                    pdfDocument ? "replace" : "document"
                  }`
                )}
              </Button>
              <input {...getInputProps()} tabIndex="0" />
              <ActionButton
                icon={<Trash color="error" />}
                onClick={() => showRemoveAlert(true)}
                color="secondary"
                size="small"
                name={t("views.pdf_viewer.import.remove")}
                tooltip
                disabled={!pdfDocument}
                className={classes.removeButton}
                classes={{
                  label: "opaque"
                }}
              />
              <DocumentRemoval
                projectUuid={estimateUuid}
                visible={removeAlertVisible}
                onClose={() => showRemoveAlert(false)}
              />
              <Divider orientation="vertical" className={classes.divider} />
              <Alert
                title={t("errors.file.too_large", { mb: PDF_PROPOSAL_MB })}
                isOpen={uploadAlertVisible}
                acceptText={t("common.close")}
                onAccept={() => showUploadAlert(false)}
              />
              {/*<PopMenu*/}
              {/*  anchor={importMenuAnchor}*/}
              {/*  show={importMenuVisible}*/}
              {/*  onClickAway={() => showImportMenu(false)}*/}
              {/*>*/}
              {/*  <MenuButton onClick={() => handleImport()}>*/}
              {/*    {t("views.pdf_viewer.import.replace")}*/}
              {/*  </MenuButton>*/}
              {/*  <MenuButton onClick={() => handleImport(true)}>*/}
              {/*    {t("views.pdf_viewer.import.append")}*/}
              {/*  </MenuButton>*/}
              {/*</PopMenu>*/}
            </>
          }
          <TextField
            size="small"
            variant="outlined"
            className={classes.input}
            value={localCurrentPage}
            label=""
            fullWidth={false}
            onFocus={() => setCurrentPage("")}
            onChange={handleCurrentPageChange}
            onBlur={handlePageInputBlur}
          />
          <span
            className="text-md"
            style={{
              minWidth: numPages.toString().length + 2 + "ch",
            }}
          >
            {"/ " + numPages}
          </span>
          <IconButton
            onClick={handleScaleButton(-1)}
            disabled={!pdfDocument}
          >
            <Remove />
          </IconButton>
          <TextField
            select
            variant="outlined"
            className={classes.select}
            value={scaleValue}
            label=""
            min={1}
            max={pdfPages.length}
            onChange={handleScaleChange}
            disabled={!pdfDocument}
          >
            {Object.values(SCALE_PRESETS).includes(
              scaleValue
            ) && (
              <MenuItem value={scaleValue} className="hidden">
                {Math.round(displayScale * 100)}%
              </MenuItem>
            )}
            <MenuItem value={0.5}>50%</MenuItem>
            <MenuItem value={0.75}>75%</MenuItem>
            <MenuItem value={1}>100%</MenuItem>
            <MenuItem value={1.25}>125%</MenuItem>
            <MenuItem value={1.5}>150%</MenuItem>
            <MenuItem value={2}>200%</MenuItem>
            <MenuItem value={3}>300%</MenuItem>
            <MenuItem value={4}>400%</MenuItem>
          </TextField>
          <IconButton
            onClick={handleScaleButton(1)}
            disabled={!pdfDocument}
          >
            <Add />
          </IconButton>
          <Divider orientation="vertical" className={classes.divider} />
          <Tooltip
            title={t(
              `views.pdf_viewer.scale.${
                scaleValue === "page-width" ? "page_width" : "page_fit"
              }`
            )}
          >
          <span>
            <IconButton
              onClick={handleScaleSet(
                scaleValue === "page-fit" ? "page-width" : "page-fit"
              )}
              disabled={!pdfDocument}
            >
              {
                scaleValue === "page-width"
                  ? <AspectRatioHorizontal />
                  : <AspectRatioVertical />
              }
            </IconButton>
          </span>
          </Tooltip>
        </Grid>
        <Grid
          item
          container
          justifyContent="flex-end"
          alignItems="center"
          wrap="nowrap"
          className={classes.buttonContainer}
        >
          {
            noShareActions && Boolean(allVisibleComments?.length) &&
            <CommentsDrawer size="medium" className="m-1" />
          }
          <Exports
            size="medium"
            className="m-1"
            sharedExport={isProposal && !hasSharePrivileges}
          />
          {
            !noShareActions && hasSharePrivileges &&
            <Share
              pdfShare={!proposalSharing}
              size="medium"
              className="m-1"
            />
          }
        </Grid>
      </Grid>
      <FeatureAlert />
    </Paper>
  );
});

ToolBar.propTypes = {
  proposalSharing: bool,
  noThumbButton: bool,
  noFileAppends: bool,
  isObserver: bool,
  hasSharePrivileges: bool,
  sidebarVisible: bool,
  handleShowSidebar: func.isRequired,
};
